import { computed, Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { none, Option } from '@sapphire/result';

import {
  mapSurveyLocalization,
  Qualification,
  SelectableQualificationDatum,
  SurveyLocaleData,
  AdvancedQualification,
} from '@ps/survey-utils';
import { Undefinable } from '@ps/utils';

import {
  selectAdvancedFeasibilityFilters,
  selectSurveyClickBalancing,
  selectSurveyId,
  selectSurvey,
  selectError,
  selectLocationFileUpload,
  selectQuotasForm,
  selectLinkedTagIds,
  selectLinkTagsError,
  selectIsDirty,
} from './audience/survey-creation-audience.selectors';
import {
  selectScorePercentage,
  selectScoreLoading,
  selectScoreError,
  selectFeasibilityLoading,
  selectFeasibilityError,
  selectSimilarSurveys,
  selectSimilarSurveysError,
  selectSimilarSurveysGlobalSearchUsed,
  selectSimilarSurveysHasSearched,
  selectSimilarSurveysSearchedGlobally,
  selectSimilarSurveysStale,
  selectSimilarSurveysStatus,
  selectFeasibilityCpi,
  selectFeasibilitySuppliers,
  selectFeasibility,
  selectAdvancedFiltersLookup,
  selectFlexibilityPrices,
  selectFlexibilityPricesError,
} from './insights/survey-creation-insights.selectors';
import {
  selectQualificationsRecord,
  selectQuestionCategories,
  selectIsQuestionCategoriesLoading,
  selectQuestionsRecord,
  selectQuestionsLoadingKey,
  selectLocationConditionsRecord,
  selectLocationConditionsLoadingKey,
} from './reference/survey-creation-reference.selectors';
import {
  locationConditionsCacheKey,
  mostRecentCacheKey,
  surveyAdvancedQualificationCacheKey,
} from '../utils';

@Injectable({ providedIn: 'root' })
export class SurveyCreationSelectorsFacade {
  private readonly _store = inject(Store);

  public readonly surveyId = this._store.selectSignal(selectSurveyId);

  // State holds `Survey | null` (Option is not serializable); the facade is the
  // boundary where that becomes an Option for everything above it.
  private readonly _survey = this._store.selectSignal(selectSurvey);
  // Data used to populate the form
  public readonly survey = computed(() => Option.from(this._survey()));

  public readonly error = this._store.selectSignal(selectError);

  public readonly surveyClickBalancing = this._store.selectSignal(
    selectSurveyClickBalancing,
  );

  private readonly _quotasForm = this._store.selectSignal(selectQuotasForm);
  public readonly hasQuotas = computed(
    () => this._quotasForm().value.length > 0,
  );

  public readonly locationFileUpload = this._store.selectSignal(
    selectLocationFileUpload,
  );

  public readonly linkedTagIds = this._store.selectSignal(selectLinkedTagIds);

  public readonly linkTagsError = this._store.selectSignal(selectLinkTagsError);

  //SECTION Score
  public readonly scorePercentage = this._store.selectSignal(
    selectScorePercentage,
  );

  public readonly scoreLoading = this._store.selectSignal(selectScoreLoading);

  public readonly scoreError = this._store.selectSignal(selectScoreError);
  //!SECTION

  //SECTION Feasibility
  public readonly feasibilityLoading = this._store.selectSignal(
    selectFeasibilityLoading,
  );

  public readonly feasibilityError = this._store.selectSignal(
    selectFeasibilityError,
  );

  public readonly feasibilityCpi =
    this._store.selectSignal(selectFeasibilityCpi);

  public readonly feasibilitySuppliers = this._store.selectSignal(
    selectFeasibilitySuppliers,
  );

  /** The estimate the last recalculation produced. */
  public readonly feasibility = this._store.selectSignal(selectFeasibility);

  public readonly advancedFilters = this._store.selectSignal(
    selectAdvancedFeasibilityFilters,
  );

  public readonly advancedFiltersLookup = this._store.selectSignal(
    selectAdvancedFiltersLookup,
  );
  //!SECTION

  //SECTION Similar Surveys
  public readonly similarSurveys =
    this._store.selectSignal(selectSimilarSurveys);

  private readonly _similarSurveysStatus = this._store.selectSignal(
    selectSimilarSurveysStatus,
  );

  public readonly similarSurveysLoading = computed(
    () => this._similarSurveysStatus() === 'loading',
  );

  public readonly similarSurveysError = this._store.selectSignal(
    selectSimilarSurveysError,
  );

  public readonly similarSurveysStale = this._store.selectSignal(
    selectSimilarSurveysStale,
  );

  public readonly similarSurveysHasSearched = this._store.selectSignal(
    selectSimilarSurveysHasSearched,
  );

  public readonly similarSurveysSearchedGlobally = this._store.selectSignal(
    selectSimilarSurveysSearchedGlobally,
  );

  public readonly similarSurveysGlobalSearchUsed = this._store.selectSignal(
    selectSimilarSurveysGlobalSearchUsed,
  );
  //!SECTION

  //SECTION Flexibility Prices
  private readonly _flexibilityPrices = this._store.selectSignal(
    selectFlexibilityPrices,
  );
  public readonly flexibilityPrices = computed(() =>
    Option.from(this._flexibilityPrices()),
  );

  public readonly flexibilityPricesError = this._store.selectSignal(
    selectFlexibilityPricesError,
  );

  public readonly isDirty = this._store.selectSignal(selectIsDirty);

  //!SECTION

  private readonly _qualificationsRecord = this._store.selectSignal(
    selectQualificationsRecord,
  );

  // Advanced Targeting (definition builder question search) reference data
  public readonly questionCategories = this._store.selectSignal(
    selectQuestionCategories,
  );

  public readonly isQuestionCategoriesLoading = this._store.selectSignal(
    selectIsQuestionCategoriesLoading,
  );

  private readonly _questionsRecord = this._store.selectSignal(
    selectQuestionsRecord,
  );

  private readonly _questionsLoadingKey = this._store.selectSignal(
    selectQuestionsLoadingKey,
  );

  private readonly _locationConditionsRecord = this._store.selectSignal(
    selectLocationConditionsRecord,
  );

  private readonly _locationConditionsLoadingKey = this._store.selectSignal(
    selectLocationConditionsLoadingKey,
  );

  // Selector methods
  public selectQuestions(
    search: string,
    categoryIds: number[],
    locale: string,
  ): AdvancedQualification[] {
    return (
      this._questionsRecord()[
        surveyAdvancedQualificationCacheKey(search, categoryIds, locale)
      ]?.data ?? []
    );
  }

  public isQuestionsLoading(
    search: string,
    categoryIds: number[],
    locale: string,
  ): boolean {
    return (
      this._questionsLoadingKey() ===
      surveyAdvancedQualificationCacheKey(search, categoryIds, locale)
    );
  }

  public selectLocationConditions(
    dbQualificationCode: number,
    locale: string,
  ): SelectableQualificationDatum[] {
    return (
      this._locationConditionsRecord()[
        locationConditionsCacheKey(dbQualificationCode, locale)
      ]?.data ?? []
    );
  }

  public isLocationConditionsLoading(
    dbQualificationCode: number,
    locale: string,
  ): boolean {
    return (
      this._locationConditionsLoadingKey() ===
      locationConditionsCacheKey(dbQualificationCode, locale)
    );
  }

  // Presence, not data length: a qualification whose lookup genuinely returns zero rows is still
  // "already tried" and must not keep re-triggering a load (see loadLocationConditions$'s cache
  // gate, which treats a fresh empty result as a valid hit the same way).
  public isLocationConditionsCached(
    dbQualificationCode: number,
    locale: string,
  ): boolean {
    return (
      locationConditionsCacheKey(dbQualificationCode, locale) in
      this._locationConditionsRecord()
    );
  }

  public selectQualifications(
    locale: Undefinable<SurveyLocaleData>,
    recentLocaleWhenNull = false,
  ): Qualification[] {
    return Option.from(locale)
      .map((value) =>
        mapSurveyLocalization(value.countryCode, value.languageShortCode),
      )
      .orElse(() =>
        recentLocaleWhenNull
          ? Option.from(
              mostRecentCacheKey(
                Object.fromEntries(
                  Object.entries(this._qualificationsRecord() ?? {}).map(
                    ([key, entry]) => [key, entry.cachedAt],
                  ),
                ),
              ),
            )
          : none,
      )
      .mapOr(
        [],
        (key: string) => this._qualificationsRecord()?.[key]?.data || [],
      );
  }
}
